/**
 * Utilities for calculating WCAG 2.0 contrast ratios between colors.
 * Based on the Web Content Accessibility Guidelines (WCAG) 2.0.
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */

export interface RGB {
    r: number; // 0-255
    g: number; // 0-255
    b: number; // 0-255
}

export interface RGBA extends RGB {
    a: number; // 0-1
}

/**
 * Converts an 8-bit RGB color component (0-255) to a normalized value (0-1)
 * and applies gamma correction for sRGB color space.
 *
 * @param component - RGB color component value (0-255)
 * @returns Gamma-corrected component value (0-1)
 */
function gammaCorrect(component: number): number {
    const normalized = component / 255;
    if (normalized <= 0.03928) {
        return normalized / 12.92;
    }
    return Math.pow((normalized + 0.055) / 1.055, 2.4);
}

/**
 * Calculates the relative luminance of a color according to WCAG 2.0.
 * Luminance is the measure of brightness of a color.
 *
 * @param rgb - RGB color object with r, g, b values (0-255)
 * @returns Relative luminance value (0-1)
 */
export function calculateLuminance(rgb: RGB): number {
    const r = gammaCorrect(rgb.r);
    const g = gammaCorrect(rgb.g);
    const b = gammaCorrect(rgb.b);

    // WCAG luminance formula: 0.2126 * R + 0.7152 * G + 0.0722 * B
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculates the contrast ratio between two colors according to WCAG 2.0.
 * The contrast ratio is defined as (L1 + 0.05) / (L2 + 0.05), where L1 is the
 * relative luminance of the lighter color and L2 is the relative luminance of
 * the darker color.
 *
 * @param color1 - First RGB color
 * @param color2 - Second RGB color
 * @returns Contrast ratio (1-21)
 */
export function calculateContrastRatio(color1: RGB, color2: RGB): number {
    const luminance1 = calculateLuminance(color1);
    const luminance2 = calculateLuminance(color2);

    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);

    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Converts a hex color string to RGB.
 *
 * @param hex - Hex color string (e.g., '#FF5733', '#F57', 'FF5733')
 * @returns RGB color object
 * @throws Error if hex string is invalid
 */
export function hexToRgb(hex: string): RGB {
    // Remove '#' if present
    const cleanHex = hex.replace(/^#/, '');

    // Handle 3-character hex
    if (cleanHex.length === 3) {
        const r = parseInt(cleanHex[0] + cleanHex[0], 16);
        const g = parseInt(cleanHex[1] + cleanHex[1], 16);
        const b = parseInt(cleanHex[2] + cleanHex[2], 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            throw new Error(`Invalid hex color: ${hex}`);
        }
        return { r, g, b };
    }

    // Handle 6-character hex
    if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            throw new Error(`Invalid hex color: ${hex}`);
        }
        return { r, g, b };
    }

    throw new Error(`Invalid hex color: ${hex}`);
}

/**
 * Calculates the average color from an array of RGB colors.
 *
 * @param colors - Array of RGB colors
 * @returns Average RGB color
 */
export function averageColor(colors: RGB[]): RGB {
    if (colors.length === 0) {
        throw new Error('Cannot calculate average of empty array');
    }

    const sum = colors.reduce(
        (acc, color) => ({
            r: acc.r + color.r,
            g: acc.g + color.g,
            b: acc.b + color.b,
        }),
        { r: 0, g: 0, b: 0 },
    );

    return {
        r: Math.round(sum.r / colors.length),
        g: Math.round(sum.g / colors.length),
        b: Math.round(sum.b / colors.length),
    };
}

/**
 * WCAG "large text" is 18pt (≈24px) or 14pt bold. Font sizes here are in the video's
 * reference-resolution space, and every Luminary reference resolution is 1080p-class
 * (1920×1080 / 1080×1080 / 1080×1920 / 1080×1350), where real display text sits well above 24 —
 * so in practice this classifies nearly ALL video text as "large" and holds it to 3:1 rather than
 * 4.5:1; only genuine fine print (disclaimers) falls under and keeps the 4.5:1 bar. This is a
 * deliberate loosening of the effective contrast threshold for video text, chosen to stop the
 * AP-926 false-positive doom loops where a legible large title kept tripping the 4.5:1 check.
 */
export const LARGE_TEXT_MIN_FONT_SIZE = 24;

/**
 * Whether a font size qualifies as WCAG "large text", which is held to a more lenient contrast
 * threshold (3:1 instead of 4.5:1).
 *
 * @param fontSize - Font size in reference-resolution pixels (Luminary's `textSize`)
 */
export function isLargeFontSize(fontSize: number): boolean {
    return fontSize >= LARGE_TEXT_MIN_FONT_SIZE;
}

/**
 * The AA contrast ratio a given text tier must meet: 3:1 for large text, 4.5:1 for normal text.
 */
export function wcagAAThreshold(isLargeText: boolean): number {
    return isLargeText ? 3 : 4.5;
}

/**
 * Determines if a contrast ratio meets WCAG 2.0 level AA standards.
 *
 * @param contrastRatio - The contrast ratio to check
 * @param isLargeText - Whether the text is considered large (18pt+ or 14pt+ bold)
 * @returns true if contrast meets AA standards
 */
export function meetsWCAG_AA(contrastRatio: number, isLargeText: boolean = false): boolean {
    return contrastRatio >= wcagAAThreshold(isLargeText);
}

/**
 * Extracts RGB colors from ImageData pixel data.
 *
 * @param imageData - ImageData object from canvas context
 * @param sampleRate - Sample every Nth pixel (default: 1 = sample all pixels)
 * @returns Array of RGB colors
 */
export function extractColorsFromImageData(imageData: ImageData, sampleRate: number = 1): RGB[] {
    const colors: RGB[] = [];
    const data = imageData.data;

    // ImageData.data is a Uint8ClampedArray with [r, g, b, a, r, g, b, a, ...]
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
        colors.push({
            r: data[i],
            g: data[i + 1],
            b: data[i + 2],
        });
    }

    return colors;
}
